import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './users.css'
import * as XLSX from 'xlsx';
import axios from 'axios';
import Cookies from 'js-cookie';
import { ClipLoader } from "react-spinners";
import { toast } from 'sonner';
import { useAbility } from '../../roles/AbilityContext';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Company, UserData, Vessels } from '../../interfaces/interfaces';
import { deleteUser, getCompany, getProfile, getVessel, patchUpdateUser, postRegister } from '../../services/requests';
import Table, { ColumnConfig } from '../../components/table/table';

const Users: React.FC = () => {
    const nempresa = Cookies.get('Nempresa');
    const [loading, setLoading] = useState(true);
    const [loadingBtn, setLoadingBtn] = useState(false);
    const ability = useAbility();
    const [data, setData] = useState<UserData[]>([]);
    const [dataVessel, setDataVessel] = useState<Vessels[]>([]);
    const [filteredDataVessel, setFilteredDataVessel] = useState(dataVessel);
    const [dataCompany, setDataCompany] = useState<Company[]>([]);
    const [filteredDataCompany, setFilteredDataCompany] = useState(dataCompany);
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Obtiene datos de empresa, embarcación y perfil del usuario desde la API.
    // Requiere: token, apiUrl, nempresa, nembarcacion. Actualiza los estados de datos filtrados.    
    const getInformation = useCallback(async () => {
        try {

            const responseCompany = await getCompany();

            const responseVessel = await getVessel();

            const responseProfile = await getProfile(Cookies.get('token'));

            if (responseProfile.status >= 200 && responseProfile.status < 300) {
                toast.success(`${responseProfile.data.message}`);
            }

            const transformedData = (responseProfile.data.data as UserData[]).map((item) => ({
                id: item.id,
                Tcontacto: item.Tcontacto,
                user: item.user,
                Nempresa: item.Nempresa,
                Nembarcacion: item.Nembarcacion || null,
                Rol: item.Rol
            }));

            setData(transformedData);
            setFilteredData(transformedData);

            const filteredCompany = (responseCompany.data.data as Company[]).filter(company => nempresa === '' || company.Nempresa === Number(nempresa))
            setDataCompany(filteredCompany);
            setFilteredDataCompany(filteredCompany);

            const filteredVessel = (responseVessel.data.data as Vessels[]).filter(vessel => nempresa === '' || vessel.Nempresa === Number(nempresa))
            setDataVessel(filteredVessel);
            setFilteredDataVessel(filteredVessel);

        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || error;
            toast.error(`${errorMessage}`);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }, [nempresa]);

    // Ejecuta acciones según los permisos del usuario.
    // Admin carga datos; Manager y User son redirigidos a sus respectivas páginas.
    useEffect(() => {
        if (ability.can('read', 'AdminPage')) {
            getInformation();
        } else if (ability.can('read', 'ManagerPage')) {
            navigate('/SmartPort/Home');
        } else if (ability.can('read', 'UserPage')) {
            navigate('/SmartPort/Company');
        }
    }, [getInformation, ability, navigate]);

    const [isFullScreen, setIsFullScreen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState(data);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const totalPage = itemsPerPage === filteredData.length ? 1 : Math.ceil(filteredData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = itemsPerPage === filteredData.length ? filteredData.length : startIndex + itemsPerPage;
    const currentData = filteredData.slice(startIndex, endIndex);

    // Avanza a la siguiente página si no se ha llegado al final.
    const handleNextPage = () => {
        if (currentPage < totalPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Retrocede a la página anterior si no se está en la primera.
    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Genera un archivo Excel (.xlsx) con los datos visibles y lo descarga.
    const downloadDataAsXLSX = () => {
        const flattenedData = data.map((item) => ({
            id: item.id,
            Tcontacto: item.Tcontacto,
            username: item.user?.username || "",
            first_name: item.user?.first_name || "",
            last_name: item.user?.last_name || "",
            email: item.user?.email || "",
            empresa: item.Nempresa?.Tempresa || "",
            embarcacion: item.Nembarcacion?.Tembarcacion || "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(flattenedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Users Data");
        XLSX.writeFile(workbook, "Users Data.xlsx");
    };

    // Filtra los datos según el término de búsqueda ingresado por el usuario.
    const handleSearch = () => {
        const filtered = data.filter((item) => {
            const values = [
                item.id?.toString() || "",
                item.Tcontacto?.toLowerCase() || "",
                item.user?.username?.toLowerCase() || "",
                item.user?.first_name?.toLowerCase() || "",
                item.user?.last_name?.toLowerCase() || "",
                item.user?.email?.toLowerCase() || "",
                item.Nempresa?.Tempresa?.toLowerCase() || "",
                item.Nempresa?.Tcontacto?.toLowerCase() || "",
                item.Nempresa?.Tcorreo?.toLowerCase() || "",
                item.Nembarcacion?.Tembarcacion?.toLowerCase() || "",
            ];

            return values.some(value => value.includes(searchTerm.toLowerCase()));
        });

        setFilteredData(filtered);
        setCurrentPage(filtered.length > 0 ? 1 : 0);
    };

    // Ordena los datos por ID en orden descendente cada vez que cambian los datos.
    useEffect(() => {
        const sortedData = [...data].sort((a, b) => (a.id < b.id ? 1 : -1));
        setFilteredData(sortedData);
    }, [data]);

    const [modalUsernameNew, setModalUsernameNew] = useState('');
    const [modalFirstNameNew, setModalFirstNameNew] = useState('');
    const [modalLastNameNew, setModalLastNameNew] = useState('');
    const [modalPhoneNumberNew, setModalPhoneNumberNew] = useState('');
    const [modalPhoneExtensionNew, setModalPhoneExtensionNew] = useState('');
    const [modalCountryCodeNew, setModalCountryCodeNew] = useState<string>("+");
    const [modalEmailNew, setModalEmailNew] = useState('');
    const [modalPasswordNew, setModalPasswordNew] = useState('');
    const [modalCompanyNew, setModalCompanyNew] = useState('');
    const [modalVesselNew, setModalVesselNew] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [modalUsername, setModalUsername] = useState('');
    const [modalFirstName, setModalFirstName] = useState('');
    const [modalLastName, setModalLastName] = useState('');
    const [modalPhoneNumber, setModalPhoneNumber] = useState('');
    const [modalPhoneExtension, setModalPhoneExtension] = useState('');
    const [modalCountryCode, setModalCountryCode] = useState<string>("+");
    const [modalEmail, setModalEmail] = useState('');
    const [modalPassword, setModalPassword] = useState('');
    const [modalConfirmPassword, setModalConfirmPassword] = useState('');
    const [modalCompany, setModalCompany] = useState('');
    const [modalVessel, setModalVessel] = useState('');

    const originalConsoleError = console.error;
    console.error = (...args) => {
        if (typeof args[0] === "string" && args[0].includes("Expected the initial `value` to be a E.164 phone number")) {
            return;
        }
        originalConsoleError(...args);
    };

    // Muestra el modal cuando se hace clic en una tarjeta.
    const handleCardClick = () => {
        setShowModal(true);
    };

    /**
     * Restablece y cierra todos los modales relacionados con la gestión de usuarios.
     * - Oculta los modales de creación, edición y eliminación.
     * - Limpia todos los campos del formulario y errores asociados.
     * - Restaura la lista de embarcaciones filtradas a su estado original.
     * - Desactiva el botón de carga.
     */
    const closeModal = () => {
        setShowModal(false);
        setShowModalEdit(false);
        setShowModalDelete(false);
        setSelectedID('');
        setModalID('');
        setModalUsername('');
        setModalFirstName('');
        setModalLastName('');
        setModalPhoneExtension('');
        setModalPhoneNumber('');
        setModalCountryCode('');
        setModalEmail('');
        setModalPassword('');
        setModalCompany('');
        setModalVessel('');
        setFilteredDataVessel(dataVessel);
        setErrorUsername('');
        setErrorFirstName('');
        setErrorLastName('');
        setErrorEmail('');
        setErrorCountryCode('');
        setErrorPhoneNumber('');
        setErrorPhoneExtension('');
        setErrorPassword('');
        setErrorConfirmPassword('');
        setErrorNempresa('');
        setErrorNembarcacion('');
        setLoadingBtn(false);
    };

    // Cierra el modal si se hace clic fuera del modal (en la superposición) y reinicia los valores del formulario.
    const handleOverlayClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).classList.contains('modalOverlay')) {
            closeModal();
        }
    };

    /**
     * Extrae un mensaje de error de un campo que puede ser un arreglo o una cadena.
     * - Si `field` es un arreglo, retorna el primer elemento.
     * - Si `field` es una cadena, la retorna directamente.
     * - Si no cumple ninguna condición, retorna una cadena vacía.
     *
     * @param field - Campo que contiene el mensaje de error (puede ser un string o un array).
     * @returns Mensaje de error como cadena.
     */
    const extractErrorMessage = (field: any) => {
        if (Array.isArray(field)) return field[0];
        if (typeof field === 'string') return field;
        return '';
    };

    // Crea un nuevo usuario después de validar los campos y los datos de entrada.
    const handleCreateUser = async () => {
        setLoadingBtn(true);

        if (!modalUsernameNew || !modalFirstNameNew || !modalLastNameNew || !modalEmailNew || !modalPasswordNew || !modalConfirmPassword) {
            setErrorUsername(!modalUsernameNew ? "Por favor complete este campo" : "");
            setErrorFirstName(!modalFirstNameNew ? "Por favor complete este campo" : "");
            setErrorLastName(!modalLastNameNew ? "Por favor complete este campo" : "");
            setErrorEmail(!modalEmailNew ? "Por favor complete este campo" : "");
            setErrorPassword(!modalPasswordNew ? "Por favor complete este campo" : "");
            setErrorConfirmPassword(!modalConfirmPassword ? "Por favor complete este campo" : "");
            toast.error(`${t('users.pleaseCompleteAllFields')}.`);
            setLoadingBtn(false);
            return;
        }

        if (modalPasswordNew !== modalConfirmPassword) {
            setErrorPassword("Contraseñas no coinciden");
            setErrorConfirmPassword("Contraseñas no coinciden");
            toast.error(`${t('users.thePasswordsAreNotTheSame')}.`);
            setLoadingBtn(false);
            return;
        }

        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[^\s]{8,}$/;

        if (!passwordRegex.test(modalPasswordNew)) {
            setErrorPassword("Contraseña inválida");
            toast.error(`${t('users.thePasswordMustHaveAtLeast8CharactersOneUppercaseOneLowercaseOneNumberAndNotContainSpaces')}.`);
            setLoadingBtn(false);
            return;
        }

        let number;

        if (!modalPhoneExtensionNew && modalPhoneNumberNew && modalCountryCodeNew) {
            number = modalCountryCodeNew + " " + modalPhoneNumberNew
        } else if (modalPhoneExtensionNew && modalPhoneNumberNew && modalCountryCodeNew) {
            number = modalCountryCodeNew + " " + modalPhoneNumberNew + " ext " + modalPhoneExtensionNew
        } else {
            number = null
        }

        try {
            const companyMatch = dataCompany.find(item => item.Tempresa === modalCompanyNew) || null;
            const vesselMatch = dataVessel.find(item => item.Tembarcacion === modalVesselNew) || null;

            const body = {
                username: modalUsernameNew,
                email: modalEmailNew,
                password: modalPasswordNew,
                firstname: modalFirstNameNew,
                lastname: modalLastNameNew,
                Tcontacto: number,
                Nempresa: modalCompanyNew ? companyMatch?.Nempresa || null : null,
                Nembarcacion: modalVesselNew ? vesselMatch?.Nembarcacion || null : null
            };

            const response = await postRegister(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('users.theResponseWasNotSuccessful')}: ${response.status}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('users.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorUsername(extractErrorMessage(errorData.username));
            setErrorFirstName(extractErrorMessage(errorData.first_name));
            setErrorLastName(extractErrorMessage(errorData.last_name));
            setErrorPhoneNumber(extractErrorMessage(errorData.Tcontacto));
            setErrorEmail(extractErrorMessage(errorData.email));
            setErrorPassword(extractErrorMessage(errorData.password));
            setErrorNempresa(extractErrorMessage(errorData.Nempresa));
            setErrorNembarcacion(extractErrorMessage(errorData.Nembarcacion));

            toast.error(`${message}`, {
                position: "top-right"
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    const [sortConfig, setSortConfig] = useState<{
        key: string | null;
        direction: "asc" | "desc" | null;
    }>({ key: "user.username", direction: "asc" });

    // Ordena los datos basados en la clave proporcionada y la dirección configurada.
    const sortData = (key: string) => {
        if (!data || data.length === 0) return;

        let direction: "asc" | "desc" = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });

        const getValue = (obj: any, path: string) => {
            return path.split(".").reduce((acc, part) => acc?.[part], obj);
        };

        const sortedData = [...data].sort((a, b) => {
            const aValue = getValue(a, key);
            const bValue = getValue(b, key);

            if (typeof aValue === "string" && typeof bValue === "string") {
                return direction === "asc"
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            if (typeof aValue === "number" && typeof bValue === "number") {
                return direction === "asc"
                    ? aValue - bValue
                    : bValue - aValue;
            }

            return 0;
        });

        setFilteredData(sortedData);
    };

    const [showModalEdit, setShowModalEdit] = useState<boolean>(false);
    const [modalID, setModalID] = useState('');
    const [selectedID, setSelectedID] = useState('');

    // Actualiza la información del usuario después de validar los campos y enviar la solicitud al servidor.
    const handleUpdateUser = async () => {
        setLoadingBtn(true);

        if (!modalID || !modalUsername || !modalFirstName || !modalLastName || !modalEmail) {
            setErrorUsername(!modalUsername ? "Por favor complete este campo" : "");
            setErrorFirstName(!modalFirstName ? "Por favor complete este campo" : "");
            setErrorLastName(!modalLastName ? "Por favor complete este campo" : "");
            setErrorEmail(!modalEmail ? "Por favor complete este campo" : "");
            toast.error(`${t('users.pleaseCompleteAllFields')}.`);
            setLoadingBtn(false);
            return;
        }

        if (modalPassword !== modalConfirmPassword) {
            setErrorPassword("Contraseñas no coinciden");
            setErrorConfirmPassword("Contraseñas no coinciden");
            toast.error(`${t('users.thePasswordsAreNotTheSame')}.`);
            setLoadingBtn(false);
            return;
        }

        let number;

        if (!modalPhoneExtension && modalPhoneNumber && modalCountryCode) {
            number = modalCountryCode + " " + modalPhoneNumber
        } else if (modalPhoneExtension && modalPhoneNumber && modalCountryCode) {
            number = modalCountryCode + " " + modalPhoneNumber + "ext" + modalPhoneExtension
        } else {
            number = null
        }

        try {
            const companyMatch = dataCompany.find(item => item.Tempresa === modalCompany) || null;
            const vesselMatch = dataVessel.find(item => item.Tembarcacion === modalVessel) || null;

            const body = {
                id: modalID,
                username: modalUsername,
                email: modalEmail,
                firstname: modalFirstName,
                lastname: modalLastName,
                Tcontacto: number,
                Nempresa: modalCompany ? companyMatch?.Nempresa || null : null,
                Nembarcacion: modalVessel ? vesselMatch?.Nembarcacion || null : null,
                ...(modalPassword ? { password: modalPassword } : {})
            };

            const response = await patchUpdateUser(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('users.theUserWithIDCouldNotBeEdited')}: ${selectedID}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('users.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorUsername(extractErrorMessage(errorData.username));
            setErrorFirstName(extractErrorMessage(errorData.first_name));
            setErrorLastName(extractErrorMessage(errorData.last_name));
            setErrorPhoneNumber(extractErrorMessage(errorData.Tcontacto));
            setErrorEmail(extractErrorMessage(errorData.email));
            setErrorPassword(extractErrorMessage(errorData.password));
            setErrorNempresa(extractErrorMessage(errorData.Nempresa));
            setErrorNembarcacion(extractErrorMessage(errorData.Nembarcacion));

            toast.error(`${message}`, {
                position: "top-right"
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    const [showModalDelete, setShowModalDelete] = useState(false);

    // Elimina un usuario si se confirma y actualiza la lista de usuarios.
    const handleDeleteUser = async () => {
        setLoadingBtn(true);
        if (!modalID) return;

        try {
            const data = {
                id: modalID
            };

            const response = await deleteUser(data);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.success(`${t('users.theVesselWithIDCouldNotBeDeleted')}: ${selectedID}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('users.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorUsername(extractErrorMessage(errorData.username));
            setErrorFirstName(extractErrorMessage(errorData.first_name));
            setErrorLastName(extractErrorMessage(errorData.last_name));
            setErrorPhoneNumber(extractErrorMessage(errorData.Tcontacto));
            setErrorEmail(extractErrorMessage(errorData.email));
            setErrorPassword(extractErrorMessage(errorData.password));
            setErrorNempresa(extractErrorMessage(errorData.Nempresa));
            setErrorNembarcacion(extractErrorMessage(errorData.Nembarcacion));

            toast.error(`${message}`, {
                position: "top-right"
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    const [isSelectOpenCompany, setIsSelectOpenCompany] = useState(false);
    const handleSelectFocusCompany = () => setIsSelectOpenCompany(true);
    const handleSelectBlurCompany = () => setIsSelectOpenCompany(false);

    const [isSelectOpenVessel, setIsSelectOpenVessel] = useState(false);
    const handleSelectFocusVessel = () => setIsSelectOpenVessel(true);
    const handleSelectBlurVessel = () => setIsSelectOpenVessel(false);

    // Limpia el valor de modalCountryCode si se abre un modal y existe un código de país.
    useEffect(() => {
        if ((showModalEdit || showModalDelete) && modalCountryCode) {
            const cleanValue = modalCountryCode.trim().split(" ")[0];
            setModalCountryCode(cleanValue);
        }
    }, [showModalEdit, showModalDelete, modalCountryCode]);

    useEffect(() => {
        if (showModal && modalCountryCodeNew) {
            const cleanValue = modalCountryCodeNew.trim().split(" ")[0];
            setModalCountryCodeNew(cleanValue);
        }
    }, [showModal, modalCountryCodeNew]);

    const [errorUsername, setErrorUsername] = useState('');
    const [errorFirstName, setErrorFirstName] = useState('');
    const [errorLastName, setErrorLastName] = useState('');
    const [errorEmail, setErrorEmail] = useState('');
    const [errorCountryCode, setErrorCountryCode] = useState('');
    const [errorPhoneNumber, setErrorPhoneNumber] = useState('');
    const [errorPhoneExtension, setErrorPhoneExtension] = useState('');
    const [errorPassword, setErrorPassword] = useState('');
    const [errorConfirmPassword, setErrorConfirmPassword] = useState('');
    const [errorNempresa, setErrorNempresa] = useState('');
    const [errorNembarcacion, setErrorNembarcacion] = useState('');

    const columns: ColumnConfig[] = [
        { key: 'id', label: "ID", sortable: true },
        { key: 'user.username', label: t('users.username'), sortable: true },
        { key: 'user.first_name', label: t('users.name'), sortable: true },
        { key: 'user.last_name', label: t('users.lastName'), sortable: true },
        { key: 'Tcontacto', label: t('users.numberContact'), sortable: true },
        {
            key: 'Rol',
            label: t('users.profile'),
            sortable: true,
            render: (row) => t(`users.${row.Rol.toLowerCase()}`),
        },
    ];

    useEffect(() => {
        if (showModal) {
            if (showModal && modalCountryCodeNew === "+") {
                setModalCountryCodeNew("+34");
            } else if (!showModal && !modalCountryCode) {
                setModalCountryCode("+34");
            }
        }
    }, [showModal]);

    return (
        <div className='containerUsers'>
            {loading ? (
                <div className="loading">
                    <ClipLoader color="#007bff" size={50} />
                    <p>{t('users.loadingInformation')}</p>
                </div>
            ) : (
                <>
                    <div className='title'>
                        <h4>{t('users.users')}</h4>
                        <div>
                            <button className="btn btn-dark" onClick={handleCardClick}>
                                <i className="fas fa-plus"></i> {t('users.newUser')}
                            </button>
                        </div>
                    </div>
                    <div className={`separator ${isFullScreen ? 'expanded' : ''}`}>
                        <Table
                            t={t}
                            titleKey="users.usersList"
                            placeholder='users.searchUsers'
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            handleSearch={handleSearch}
                            downloadDataAsXLSX={downloadDataAsXLSX}
                            isFullScreen={isFullScreen}
                            setIsFullScreen={setIsFullScreen}

                            nameClassTable="tableReports"
                            columns={columns}
                            currentData={currentData}
                            sortConfig={sortConfig}
                            onSort={sortData}
                            onRowEdit={(row) => {
                                setShowModalEdit(true);
                                setSelectedID(row.id.toString());
                                setModalID(row.id.toString());
                                const selectedItem = filteredData.find((data) => data.id === row.id);
                                setModalUsername(selectedItem ? selectedItem.user.username : "");
                                setModalFirstName(selectedItem ? selectedItem.user.first_name : "");
                                setModalLastName(selectedItem ? selectedItem.user.last_name : "");
                                const contacto = selectedItem?.Tcontacto ?? "";
                                const firstPart = contacto.split(' ')[0] || "";
                                const extIndex = contacto.toLowerCase().indexOf("ext");
                                const secondPart = extIndex !== -1 ? contacto.substring(contacto.indexOf(" ") + 1, extIndex).trim() : contacto.split(' ')[1] || "";
                                setModalCountryCode(firstPart + " " + secondPart);
                                setModalPhoneNumber(secondPart);
                                const thirdPart = extIndex !== -1 ? contacto.substring(extIndex + 3).trim() : "";
                                setModalPhoneExtension(thirdPart);
                                setModalEmail(selectedItem ? selectedItem.user.email : "");
                                const companyMatch = dataCompany.find(item => item.Nempresa === selectedItem?.Nempresa?.Nempresa);
                                const vesselMatch = dataVessel.find(item => item.Nembarcacion === selectedItem?.Nembarcacion?.Nembarcacion);
                                setModalCompany(companyMatch ? companyMatch.Tempresa : "");
                                setModalVessel(vesselMatch ? vesselMatch.Tembarcacion.toString() : "");
                            }}
                            onRowDelete={(row) => {
                                setShowModalDelete(true);
                                setSelectedID(row.id.toString());
                                setModalID(row.id.toString());
                                const selectedItem = filteredData.find((data) => data.id === row.id);
                                setModalUsername(selectedItem ? selectedItem.user.username : "");
                                setModalFirstName(selectedItem ? selectedItem.user.first_name : "");
                                setModalLastName(selectedItem ? selectedItem.user.last_name : "");
                                const contacto = selectedItem?.Tcontacto ?? "";
                                const firstPart = contacto.split(' ')[0] || "";
                                const extIndex = contacto.toLowerCase().indexOf("ext");
                                const secondPart = extIndex !== -1 ? contacto.substring(contacto.indexOf(" ") + 1, extIndex).trim() : contacto.split(' ')[1] || "";
                                setModalCountryCode(firstPart + " " + secondPart);
                                setModalPhoneNumber(secondPart);
                                const thirdPart = extIndex !== -1 ? contacto.substring(extIndex + 3).trim() : "";
                                setModalPhoneExtension(thirdPart);
                                setModalEmail(selectedItem ? selectedItem.user.email : "");
                                const companyMatch = dataCompany.find(item => item.Nempresa === selectedItem?.Nempresa?.Nempresa);
                                const vesselMatch = dataVessel.find(item => item.Nembarcacion === selectedItem?.Nembarcacion?.Nembarcacion);
                                setModalCompany(companyMatch ? companyMatch.Tempresa : "");
                                setModalVessel(vesselMatch ? vesselMatch.Tembarcacion.toString() : "");
                            }}

                            handlePreviousPage={handlePreviousPage}
                            currentPage={currentPage}
                            totalPage={totalPage}
                            itemsPerPage={itemsPerPage}
                            filteredData={filteredData}
                            setItemsPerPage={setItemsPerPage}
                            setCurrentPage={setCurrentPage}
                            handleNextPage={handleNextPage}
                            data={data}
                        />
                    </div>

                    {(showModal || showModalEdit || showModalDelete) && (
                        <div className='modalOverlay' onClick={handleOverlayClick}>
                            <div className='modalContent'>
                                <div className='title'>
                                    <h6>
                                        {showModal ? t('users.newUser') : showModalEdit ? t('users.editUser') : showModalDelete ? t('users.deleteUser') : ''}
                                    </h6>
                                    <span className="closeIcon" onClick={() => {
                                        closeModal();
                                    }}>&times;</span>
                                </div>
                                {!showModal &&
                                    <div className='formInput'>
                                        <div className="input-row">
                                            <h6>ID</h6>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={modalID}
                                                placeholder="Search by ID"
                                                disabled
                                            />
                                        </div>
                                    </div>
                                }
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('users.username')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorUsername ? 'is-invalid' : ''}`}
                                            value={showModal ? modalUsernameNew : modalUsername}
                                            onChange={(e) => {
                                                if (showModal) { setModalUsernameNew(e.target.value); }
                                                if (!showModal) { setModalUsername(e.target.value); }
                                                setErrorUsername('');
                                            }}
                                            placeholder={`${t('users.enterYour')} ${t('users.username').toLowerCase()}`}
                                            disabled={showModalDelete}
                                        />
                                    </div>
                                    {errorUsername && (
                                        <div className="invalid-feedback d-block">{errorUsername}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('users.name')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorFirstName ? 'is-invalid' : ''}`}
                                            value={showModal ? modalFirstNameNew : modalFirstName}
                                            onChange={(e) => {
                                                if (showModal) { setModalFirstNameNew(e.target.value); }
                                                if (!showModal) { setModalFirstName(e.target.value); }
                                                setErrorFirstName('');
                                            }}
                                            placeholder={`${t('users.enterYour')} ${t('users.name').toLowerCase()}`}
                                            disabled={showModalDelete}
                                        />
                                    </div>
                                    {errorFirstName && (
                                        <div className="invalid-feedback d-block">{errorFirstName}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('users.lastName')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorLastName ? 'is-invalid' : ''}`}
                                            value={showModal ? modalLastNameNew : modalLastName}
                                            onChange={(e) => {
                                                if (showModal) { setModalLastNameNew(e.target.value); }
                                                if (!showModal) { setModalLastName(e.target.value); }
                                                setErrorLastName('');
                                            }}
                                            placeholder={`${t('users.enterYour')} ${t('users.lastName').toLowerCase()}`}
                                            disabled={showModalDelete}
                                        />
                                    </div>
                                    {errorLastName && (
                                        <div className="invalid-feedback d-block">{errorLastName}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6> {t('users.numberContact')}</h6>
                                        <div className='phone'>
                                            <div className="form-control phone-country">
                                                <PhoneInput
                                                    defaultCountry="ES"
                                                    international
                                                    countryCallingCodeEditable={false}
                                                    value={showModal ? modalCountryCodeNew : modalCountryCode}
                                                    onChange={(value) => {
                                                        setErrorCountryCode("");
                                                        if (!value) {
                                                            if (showModal) setModalCountryCodeNew("+");
                                                            else setModalCountryCode("+");
                                                            return;
                                                        }
                                                        const numericPart = value.replace(/\D/g, "");
                                                        const formatted = "+" + numericPart;
                                                        const finalValue = formatted.slice(0, 4);
                                                        if (showModal) setModalCountryCodeNew(finalValue);
                                                        else setModalCountryCode(finalValue);
                                                    }}
                                                    readOnly={false}
                                                    disabled={showModalDelete}
                                                />
                                            </div>
                                            <input
                                                type="text"
                                                className={`form-control ${errorPhoneNumber ? 'is-invalid' : ''}`}
                                                placeholder={t('users.number')}
                                                value={showModal ? modalPhoneNumberNew : modalPhoneNumber}
                                                onChange={(e) => {
                                                    const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");
                                                    if (showModal) { setModalPhoneNumberNew(onlyNumbers); }
                                                    if (!showModal) { setModalPhoneNumber(onlyNumbers); }
                                                    setErrorPhoneNumber("");
                                                }}
                                                disabled={showModalDelete}
                                            />
                                            <input
                                                type="text"
                                                className={`form-control ${errorPhoneExtension ? 'is-invalid' : ''}`}
                                                placeholder="Ext"
                                                value={showModal ? modalPhoneExtensionNew : modalPhoneExtension}
                                                onChange={(e) => {
                                                    const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");
                                                    if (showModal) { setModalPhoneExtensionNew(onlyNumbers); }
                                                    if (!showModal) { setModalPhoneExtension(onlyNumbers); }
                                                    setErrorPhoneExtension("");
                                                }}
                                                disabled={showModalDelete}
                                            />
                                        </div>
                                        {(errorCountryCode || errorPhoneNumber || errorPhoneExtension) && (
                                            <div className="invalid-feedback d-block">{(errorCountryCode || errorPhoneNumber || errorPhoneExtension)}</div>
                                        )}
                                    </div>
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('users.email')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorEmail ? 'is-invalid' : ''}`}
                                            value={showModal ? modalEmailNew : modalEmail}
                                            onChange={(e) => {
                                                if (showModal) { setModalEmailNew(e.target.value); }
                                                if (!showModal) { setModalEmail(e.target.value); }
                                                setErrorEmail('');
                                            }}
                                            placeholder={`${t('users.enterYour')} ${t('users.email').toLowerCase()}`}
                                            disabled={showModalDelete}
                                            autoComplete="off"
                                            name="no-autofill-email"
                                            inputMode="email"
                                        />
                                    </div>
                                    {errorEmail && (
                                        <div className="invalid-feedback d-block">{errorEmail}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6>{showModal && (<span>*</span>)} {t('users.password')}</h6>
                                        <input
                                            type="password"
                                            className={`form-control ${errorPassword ? 'is-invalid' : ''}`}
                                            value={showModal ? modalPasswordNew : modalPassword}
                                            onChange={(e) => {
                                                if (showModal) { setModalPasswordNew(e.target.value); }
                                                if (!showModal) { setModalPassword(e.target.value); }
                                                setErrorPassword('');
                                            }}
                                            placeholder={`${t('users.enterYour')} ${t('users.password').toLowerCase()}`}
                                            onCopy={(e) => e.preventDefault()}
                                            onPaste={(e) => e.preventDefault()}
                                            onCut={(e) => e.preventDefault()}
                                            disabled={showModalDelete}
                                            autoComplete="new-password"
                                            name="new-password"
                                            inputMode="none"
                                        />
                                    </div>
                                    {errorPassword && (
                                        <div className="invalid-feedback d-block">{errorPassword}</div>
                                    )}
                                </div>
                                {!showModalDelete &&
                                    <div className='formInput'>
                                        <div className="input-row">
                                            <h6>{showModal && (<span>*</span>)} {t('users.confirmPassword')}</h6>
                                            <input
                                                type="password"
                                                className={`form-control ${errorConfirmPassword ? 'is-invalid' : ''}`}
                                                value={showModal ? modalConfirmPassword : ""}
                                                onChange={(e) => {
                                                    setModalConfirmPassword(e.target.value);
                                                    setErrorConfirmPassword('');
                                                }}
                                                placeholder={`${t('users.enterYour')} ${t('users.password').toLowerCase()} ${t('users.again')}`}
                                                onCopy={(e) => e.preventDefault()}
                                                onPaste={(e) => e.preventDefault()}
                                                onCut={(e) => e.preventDefault()}
                                                autoComplete="new-password"
                                                name="new-password"
                                                inputMode="none"
                                            />
                                        </div>
                                        {errorConfirmPassword && (
                                            <div className="invalid-feedback d-block">{errorConfirmPassword}</div>
                                        )}
                                    </div>
                                }
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6>{t('users.company')}</h6>
                                        <div className="select-wrapper">
                                            <select
                                                className={`form-control ${errorNempresa ? 'is-invalid' : ''}`}
                                                value={showModal ? modalCompanyNew : modalCompany}
                                                onChange={(e) => {
                                                    if (showModal) { setModalCompanyNew(e.target.value); }
                                                    if (!showModal) { setModalCompany(e.target.value); }
                                                    setErrorNempresa("");

                                                    if (e.target.value) {
                                                        const filteredCompany = dataCompany.filter(company => company.Tempresa === e.target.value);
                                                        const filteredVessel = dataVessel.filter(vessel => vessel.Nempresa === filteredCompany[0].Nempresa);
                                                        setFilteredDataVessel(filteredVessel)
                                                    } else {
                                                        setFilteredDataVessel(dataVessel)
                                                    }
                                                }}
                                                onFocus={handleSelectFocusCompany}
                                                onBlur={handleSelectBlurCompany}
                                                disabled={showModalDelete}
                                            >
                                                <option value="">{t('users.selectACompany')} ...</option>
                                                {filteredDataCompany
                                                    .map((item) => (
                                                        <option key={item.Nempresa} value={String(item.Tempresa)}>
                                                            {item.Tempresa}
                                                        </option>
                                                    ))}
                                            </select>
                                            <span className="icon">
                                                {isSelectOpenCompany ? (
                                                    <i className="fas fa-chevron-up"></i>
                                                ) : (
                                                    <i className="fas fa-chevron-down"></i>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    {errorNempresa && (
                                        <div className="invalid-feedback d-block">{errorNempresa}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6>{t('users.vessel')}</h6>
                                        <div className="select-wrapper">
                                            <select
                                                className={`form-control ${errorNembarcacion ? 'is-invalid' : ''}`}
                                                value={showModal ? modalVesselNew : modalVessel}
                                                onChange={(e) => {
                                                    if (showModal) { setModalVesselNew(e.target.value); }
                                                    if (!showModal) { setModalVessel(e.target.value); }
                                                    setErrorNembarcacion("");
                                                    if (e.target.value) {
                                                        const filteredVessel = dataVessel.filter(vessel => vessel.Tembarcacion === e.target.value);
                                                        const filteredCompany = dataCompany.filter(company => company.Nempresa === filteredVessel[0].Nempresa);
                                                        setModalCompanyNew(filteredCompany[0].Tempresa);
                                                    }
                                                }}
                                                onFocus={handleSelectFocusVessel}
                                                onBlur={handleSelectBlurVessel}
                                                disabled={showModalDelete}
                                            >
                                                <option value="">{t('users.selectAVessel')} ...</option>
                                                {filteredDataVessel
                                                    .map((item) => (
                                                        <option key={item.Nembarcacion} value={String(item.Tembarcacion)}>
                                                            {item.Tembarcacion}
                                                        </option>
                                                    ))}
                                            </select>
                                            <span className="icon">
                                                {isSelectOpenVessel ? (
                                                    <i className="fas fa-chevron-up"></i>
                                                ) : (
                                                    <i className="fas fa-chevron-down"></i>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    {errorNembarcacion && (
                                        <div className="invalid-feedback d-block">{errorNembarcacion}</div>
                                    )}
                                </div>
                                <div className='formBtn'>
                                    <button className={showModalDelete ? 'btn btn-danger' : 'btn btn-dark'} onClick={() => {
                                        if (showModal) handleCreateUser();
                                        if (showModalEdit) handleUpdateUser();
                                        if (showModalDelete) handleDeleteUser();
                                    }}
                                        disabled={loadingBtn}>
                                        {loadingBtn ? (
                                            <div className="loading-btn">
                                                <ClipLoader color="#007bff" size={20} />
                                            </div>
                                        ) : (
                                            <h6>
                                                {showModal ? t('users.createUser') : showModalEdit ? t('users.updateUser') : showModalDelete ? t('users.deleteUser') : ''}
                                            </h6>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Users;